import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { resourcesAPI } from '../services/api';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Search, BookOpen, FileText, Lightbulb, Mail, BarChart3, Layers, ChevronRight, Shield } from 'lucide-react';
import { RESOURCE_CATEGORIES } from '../constants/resourceCategories';

import Pagination from '../components/shared/Pagination';

const NAVY = '#1e3a5f';
const ORANGE = '#ea580c';
const PAGE_SIZE = 12;

const CAT_ICONS = {
  'Process Guides':   FileText,
  'How-To Guides':    BookOpen,
  'Sales Enablement': BarChart3,
  'Email Templates':  Mail,
  'Marketing':        Lightbulb,
  'Internal Guides':  Shield,
};

const CATEGORIES = [
  { name: 'All', icon: Layers, color: '#64748b' },
  ...RESOURCE_CATEGORIES.map(c => ({ ...c, icon: CAT_ICONS[c.name] || Layers })),
];

// Fixed resources that live outside the DB (e.g. live data pages)
const FEATURED_RESOURCES = [
  {
    id: '__pricing_guide__',
    title: 'Partner Pricing Guide',
    category: 'Sales Enablement',
    description: 'Live pricing guide with product pricing, DB server upgrades, App Server costs by region and worked examples. Downloadable as a branded PDF.',
    tags: ['Pricing', 'Live', 'PDF'],
    _href: '/resources/pricing-guide',
    _state: undefined,
  },
];

function CategoryPill({ cat, selected, onClick }) {
  const Icon = cat.icon;
  const isSelected = selected === cat.name || (selected === 'All' && cat.name === 'All');
  return (
    <button onClick={onClick}
      className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all border"
      style={isSelected
        ? { background: cat.color, color: 'white', borderColor: cat.color }
        : { background: 'white', color: '#475569', borderColor: '#e2e8f0' }}>
      <Icon size={14} />
      {cat.name}
    </button>
  );
}

function ResourceCard({ resource, onClick }) {
  const cat = CATEGORIES.find(c => c.name === resource.category) || CATEGORIES[1];
  return (
    <button onClick={onClick}
      className="w-full text-left rounded-2xl border bg-white p-5 flex flex-col transition-all hover:shadow-md hover:-translate-y-0.5 group"
      style={{ minHeight: '160px' }}
      data-testid={`resource-card-${resource.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="font-bold text-sm leading-snug" style={{ color: NAVY }}>{resource.title}</p>
          <ChevronRight size={16} className="mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
            style={{ color: ORANGE }} />
        </div>
        {resource.description && (
          <p className="text-xs mt-1.5" style={{ color: '#64748b', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {resource.description}
          </p>
        )}
      </div>
      <div className="mt-3 pt-3" style={{ borderTop: '1px solid #f1f5f9' }}>
        <span className="text-xs font-medium px-2 py-0.5 rounded-full"
          style={{ background: cat.color + '15', color: cat.color }}>
          {resource.category}
        </span>
      </div>
    </button>
  );
}

export default function ResourcesPage() {
  const navigate = useNavigate();
  const [resources, setResources]   = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [category, setCategory]     = useState('All');
  const [page, setPage]             = useState(1);
  const [featuredVis, setFeaturedVis] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: 'published', page, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (category !== 'All') params.category = category;
      const [resRes, visRes] = await Promise.all([
        resourcesAPI.list(params),
        resourcesAPI.getFeaturedVisibility().catch(() => ({ data: {} })),
      ]);
      const d = resRes.data || {};
      setResources(d.items || []);
      setPagination({ total: d.total || 0, page: d.page || 1, pages: d.pages || 1 });
      setFeaturedVis(visRes.data || {});
    } catch { toast.error('Failed to load resources'); }
    finally { setLoading(false); }
  }, [search, category, page]);

  const handleSearch   = (val) => { setSearch(val);   setPage(1); };
  const handleCategory = (val) => { setCategory(val); setPage(1); };

  useEffect(() => { load(); }, [load]);

  const counts = {};
  resources.forEach(r => { counts[r.category] = (counts[r.category] || 0) + 1; });

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>
          Resource Hub
        </h1>
        <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
          Process guides, how-to documents and sales enablement assets for your team
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'hsl(var(--muted-fg))' }} />
        <Input placeholder="Search resources…" value={search}
          onChange={e => handleSearch(e.target.value)} className="pl-9 h-11 text-sm" />
      </div>

      {/* Category pills */}
      <div className="flex gap-2 flex-wrap overflow-x-auto pb-1 -mx-1 px-1">
        {CATEGORIES.map(cat => (
          <CategoryPill key={cat.name} cat={cat} selected={category}
            onClick={() => handleCategory(cat.name)} />
        ))}
      </div>

      {/* Results count */}
      {!loading && (() => {
        const featuredMatches = FEATURED_RESOURCES.filter(r =>
          (featuredVis[r._key] !== false) &&
          (category === 'All' || r.category === category) &&
          (!search || r.title.toLowerCase().includes(search.toLowerCase()) || r.description.toLowerCase().includes(search.toLowerCase()))
        );
        const totalCount = pagination.total + featuredMatches.length;
        return (
          <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
            {totalCount} resource{totalCount !== 1 ? 's' : ''}
            {category !== 'All' ? ` in ${category}` : ''}
            {search ? ` matching "${search}"` : ''}
          </p>
        );
      })()}

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-40 rounded-2xl" />)}
        </div>
      ) : (() => {
        const featuredMatches = page === 1 ? FEATURED_RESOURCES.filter(r =>
          (category === 'All' || r.category === category) &&
          (!search || r.title.toLowerCase().includes(search.toLowerCase()) || r.description.toLowerCase().includes(search.toLowerCase()))
        ) : [];
        const totalCount = pagination.total + featuredMatches.length;
        if (totalCount === 0 && page === 1) return (
          <div className="rounded-2xl border border-dashed bg-white p-12 text-center">
            <BookOpen size={28} className="mx-auto mb-3 opacity-20" />
            <p className="font-semibold" style={{ color: NAVY }}>No resources found</p>
            <p className="text-sm mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>
              {search ? 'Try a different search term.' : 'No resources have been published yet.'}
            </p>
          </div>
        );
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredMatches.map(r => (
                <ResourceCard key={r.id} resource={r} onClick={() => navigate(r._href, { state: r._state })} />
              ))}
              {resources.map(r => (
                <ResourceCard key={r.id} resource={r} onClick={() => navigate(`/resources/${r.slug || r.id}`)} />
              ))}
            </div>
            <Pagination
              page={pagination.page}
              pages={pagination.pages}
              total={pagination.total}
              limit={PAGE_SIZE}
              onChange={setPage}
            />
          </div>
        );
      })()}
    </div>
  );
}
